import { generateId } from "@fleet/utils";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { chronosRoute } from "../chronos";
import { GlassSurface, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Search, Send, Users } from "@fleet/icons";
import {
  cn,
  handleApiResponse,
  liveFeedEventFilters,
  liveReactionFilters,
  liveWorkspaceMemberFilters,
  patchRowVerified,
  queryKeys,
} from "@time-tracker/shared";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { ensureWorkspace } from "@/features/chronos-sync/workspace";
import {
  castStatus,
  formatTimestamp,
  getFeedEventContent,
  type PresenceStatus,
} from "@/features/chronos-sync/helpers";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { WorkspaceSwitcher } from "@/features/chronos-sync/components/workspace-switcher";
import { api, createApiQuery } from "@/lib/api";
import { Avatar } from "@/components/ui/avatar";
import {
  and,
  eq,
  feed_event,
  reaction,
  sql,
  user,
  workspace_member,
} from "@time-tracker/powersync";
import { showToast } from "@/components/ui/toast";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

const FEED_LIMIT = 15;

interface TeamMember {
  id: string;
  /**
   * Membership user: this is where the avatar COLOR comes from (stable even if
   * the name changes). Nullable because the column is; `<Avatar>` tolerates it
   * —`avatarPaletteIndex` falls back to "" and returns a valid index—.
   */
  user_id: string | null;
  name: string;
  role: string;
  avatar: string | null;
  status: PresenceStatus;
  activity: string;
}

interface FeedEventRow {
  id: string;
  /** Event author: this is where the avatar COLOR comes from (stable even if the name changes). */
  userId: string;
  userName: string | null;
  userAvatar: string | null;
  event_type: string | null;
  event_data: string | null;
  created_at: string | null;
}

interface ReactionRow {
  id: string;
  feed_event_id: string | null;
  user_id: string | null;
  emoji: string | null;
}

interface WorkspaceMemberRow {
  id: string;
  user_id: string | null;
  name: string | null;
  role: string | null;
  avatar: string | null;
  status: string | null;
  activity: string | null;
}

const getStatusColor = (status: TeamMember["status"]) => {
  switch (status) {
    case "online": {
      return "border-emerald-500 ring-2 ring-emerald-500/20";
    }
    case "focus": {
      return "border-[#ebb2ff] ring-2 ring-[#ebb2ff]/20";
    }
    case "away": {
      return "border-amber-500 ring-2 ring-amber-500/20";
    }
    case "offline": {
      return "border-white/10 ring-0";
    }
  }
  return "border-white/10 ring-0";
};

const getStatusBadge = (status: TeamMember["status"]) => {
  switch (status) {
    case "online": {
      return "bg-emerald-500";
    }
    case "focus": {
      return "bg-[#ebb2ff]";
    }
    case "away": {
      return "bg-amber-500";
    }
    case "offline": {
      return "bg-muted-foreground/60";
    }
  }
  return "bg-muted-foreground/60";
};

// Rebuilds the team route's own search from the merged global search,
// narrowing to literals and preserving `ws`/`wsdialog`.
function carryTeamSearch(prev: {
  ws?: string;
  wsdialog?: unknown;
  dialog?: unknown;
  q?: string;
}): TeamSearch {
  return {
    ws: prev.ws,
    wsdialog: prev.wsdialog === "new" ? "new" : undefined,
    dialog: prev.dialog === "invite" ? "invite" : undefined,
    q: prev.q,
  };
}

function TeamPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();

  const workspaceId = activeWorkspaceId;
  const [newEvent, setNewEvent] = createSignal("");
  const [isInviteOpen, setIsInviteOpen] = createSignal(false);
  const [inviteEmail, setInviteEmail] = createSignal("");
  const [inviteRole, setInviteRole] = createSignal("member");
  const [isInviting, setIsInviting] = createSignal(false);

  // Pending workspace invitations (visible only to members)
  const invitationsQuery = createApiQuery(
    () => ["workspace-invitations", workspaceId()],
    async () =>
      api.GET("/api/workspace/invitations", {
        params: { query: { workspace_id: workspaceId() } },
      }),
    () => ({ enabled: isUuid(workspaceId()), suspense: false }),
  );

  // Invitations addressed to my email (for accepting from the banner)
  const myInvitationsQuery = createApiQuery(
    () => ["workspace-invitations-mine", userId()],
    async () => api.GET("/api/workspace/invitations/mine", {}),
    () => ({ enabled: Boolean(userId()) && userId() !== "anonymous", suspense: false }),
  );

  // P23 / CC-60 · `data` is the ONLY field of the solid-query proxy that
  // suspends, and `isSuccess` is exactly its existence condition. The
  // `suspense: false` above does NOT protect: `createApiQuery` passes it to
  // `createQuery`, and the solid-query proxy does not read that option.
  const pendingInvitations = () =>
    invitationsQuery.isSuccess ? (invitationsQuery.data ?? []) : [];
  const myInvitations = () => (myInvitationsQuery.isSuccess ? (myInvitationsQuery.data ?? []) : []);

  const handleInviteSubmit = async (e: Event) => {
    e.preventDefault();
    const emailStr = inviteEmail().trim();

    if (!emailStr || !emailStr.includes("@")) {
      showToast(t(msg`Please enter a valid email`), "warning");
      return;
    }
    let targetWorkspace = workspaceId();
    if (!isUuid(targetWorkspace)) {
      const dbInstance = drizzle();
      if (!dbInstance) {
        showToast(t(msg`The workspace is not synced yet`), "warning");
        return;
      }
      targetWorkspace = await ensureWorkspace(dbInstance, userId());
    }

    setIsInviting(true);
    try {
      // CC-30: handleApiResponse throws on 4xx/5xx with the response body
      // message, so a rejected invite never passes as a silent success.
      await handleApiResponse(
        api.POST("/api/workspace/invitations", {
          body: {
            workspace_id: targetWorkspace,
            email: emailStr,
            role: inviteRole(),
          },
        }),
      );

      showToast(t(msg`Invitation sent to ${emailStr}`), "success");
      setIsInviteOpen(false);
      setInviteDialogUrl(false);
      setInviteEmail("");
      setInviteRole("member");
      void invitationsQuery.refetch();
    } catch (err) {
      console.error("Error al invitar miembro:", err);
      showToast(
        err instanceof Error ? err.message : t(msg`Could not send the invitation`),
        "error",
      );
    } finally {
      setIsInviting(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      await handleApiResponse(
        api.POST("/api/workspace/invitations/accept", {
          body: { invitation_id: invitationId },
        }),
      );
      showToast(t(msg`Invitation accepted. Syncing workspace…`), "success");
      void myInvitationsQuery.refetch();
    } catch (err) {
      console.error("Failed to accept invitation:", err);
      showToast(
        err instanceof Error ? err.message : t(msg`Could not accept the invitation`),
        "error",
      );
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await handleApiResponse(
        api.POST("/api/workspace/invitations/cancel", {
          body: { invitation_id: invitationId },
        }),
      );
      showToast(t(msg`Invitation cancelled`), "success");
      void invitationsQuery.refetch();
    } catch (err) {
      console.error("Failed to cancel invitation:", err);
      showToast(
        err instanceof Error ? err.message : t(msg`Could not cancel the invitation`),
        "error",
      );
    }
  };

  // Ensure a personal workspace + own membership (no demo data)
  createEffect(() => {
    const currentUserId = userId();
    const dbInstance = drizzle();
    if (currentUserId !== "anonymous" && dbInstance) {
      void (async () => {
        try {
          await ensureWorkspace(dbInstance, currentUserId);
        } catch (err) {
          console.error("Failed to ensure workspace", err);
        }
      })();
    }
  });

  // Query workspace members and users from SQLite
  const membersQuery = createPowerSyncQuery<WorkspaceMemberRow>({
    queryKey: () => queryKeys.chronos.team.members(userId(), workspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({
          id: workspace_member.id,
          user_id: workspace_member.user_id,
          name: user.name,
          role: workspace_member.role,
          avatar: user.image,
          status: user.current_status,
          activity: sql<
            string | null
          >`(SELECT description FROM time_entry WHERE user_id = ${user.id} AND is_running = 1 LIMIT 1)`,
        })
        .from(workspace_member)
        .innerJoin(user, eq(workspace_member.user_id, user.id))
        .where(
          and(
            eq(workspace_member.workspace_id, workspaceId()),
            sql`user.deleted_at IS NULL`,
            ...liveWorkspaceMemberFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()) && Boolean(workspaceId()),
    suspense: false,
  });

  const teamMembers = createMemo<TeamMember[]>(() => {
    const queryList = membersQuery.data ?? [];
    return queryList.map((m) => {
      const displayStatus = m.activity ? "focus" : castStatus(m.status);
      return {
        id: m.id,
        user_id: m.user_id,
        name: m.name ?? t(msg`Unknown User`),
        role: m.role ?? t(msg`Member`),
        avatar: m.avatar,
        status: displayStatus,
        activity:
          m.activity ?? (displayStatus === "away" ? t(msg`Away from keyboard`) : t(msg`Inactive`)),
      };
    });
  });

  // Deep-link support: the team filter is shareable via ?q=. URL→signal on load,
  // signal→URL on input (replace), and an effect to mirror back/forward + pasted
  // links. Idempotent: reloading the URL reconstructs the same filter.
  const search = useSearch({ from: chronosTeamRoute.id });
  const navigate = useNavigate();

  const [filterQuery, setFilterQuery] = createSignal(search().q ?? "");

  const updateFilterQuery = (value: string) => {
    setFilterQuery(value);
    const q = value.trim();
    void navigate({
      to: chronosTeamRoute.to,
      // Preserve other search params (e.g. `?ws=`, `?dialog=invite`).
      search: (prev) => ({ ...carryTeamSearch(prev), q: q === "" ? undefined : q }),
      replace: true,
    });
  };

  // Comparison inside `untrack`: react only to EXTERNAL changes to `?q=`; if it
  // tracks `filterQuery()`, typing retriggers it with the old URL and reverts
  // the input (bug #15b / CC-16).
  createEffect(() => {
    const urlQuery = search().q ?? "";
    if (untrack(() => urlQuery !== filterQuery())) {
      setFilterQuery(urlQuery);
    }
  });

  // Deep-link to the invite member dialog: ?dialog=invite. URL→state and
  // state→URL (replace). Idempotent: the link reopens the dialog in another browser.
  const setInviteDialogUrl = (open: boolean) => {
    void navigate({
      to: chronosTeamRoute.to,
      search: (prev) => ({
        ...carryTeamSearch(prev),
        dialog: open ? "invite" : undefined,
      }),
      replace: true,
    });
  };

  createEffect(() => {
    const wantOpen = search().dialog === "invite";
    if (untrack(() => wantOpen !== isInviteOpen())) {
      setIsInviteOpen(wantOpen);
    }
  });

  const openInviteDialog = () => {
    setIsInviteOpen(true);
    setInviteDialogUrl(true);
  };

  const closeInviteDialog = () => {
    setIsInviteOpen(false);
    setInviteDialogUrl(false);
  };

  const filteredTeamMembers = createMemo(() => {
    const list = teamMembers();
    const query = filterQuery().toLowerCase().trim();
    if (!query) {
      return list;
    }
    return list.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.role.toLowerCase().includes(query) ||
        m.activity.toLowerCase().includes(query),
    );
  });

  const updateMemberRole = async (memberId: string, newRole: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await patchRowVerified(dbInstance, workspace_member, workspace_member.id, memberId, {
        role: newRole,
      });

      showToast(t(msg`Member role updated to ${newRole}`), "success");
    } catch (err) {
      console.error("Failed to update member role", err);
      showToast(t(msg`Could not update the role`), "error");
    }
  };

  // Query live feed events from SQLite
  const feedEventsQuery = createPowerSyncQuery<FeedEventRow>({
    queryKey: () => queryKeys.chronos.team.feedEvents(userId(), workspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({
          id: feed_event.id,
          userId: user.id,
          userName: user.name,
          userAvatar: user.image,
          event_type: feed_event.event_type,
          event_data: feed_event.event_data,
          created_at: feed_event.created_at,
        })
        .from(feed_event)
        .innerJoin(user, eq(feed_event.user_id, user.id))
        .where(and(eq(feed_event.workspace_id, workspaceId()), ...liveFeedEventFilters()))
        .orderBy(sql`feed_event.created_at DESC`)
        .limit(FEED_LIMIT);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()) && Boolean(workspaceId()),
    suspense: false,
  });

  // Query emoji reactions from SQLite
  const reactionsQuery = createPowerSyncQuery<ReactionRow>({
    queryKey: () => queryKeys.chronos.team.reactions(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({
          id: reaction.id,
          feed_event_id: reaction.feed_event_id,
          user_id: reaction.user_id,
          emoji: reaction.emoji,
        })
        .from(reaction)
        .where(and(...liveReactionFilters()));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  const combinedFeed = createMemo(() => {
    const events = feedEventsQuery.data ?? [];
    const reactList = reactionsQuery.data ?? [];

    return events.map((evt) => {
      const eventReactions: Record<string, number> = {};
      for (const r of reactList) {
        if (r.feed_event_id === evt.id && r.emoji) {
          eventReactions[r.emoji] = (eventReactions[r.emoji] ?? 0) + 1;
        }
      }

      return {
        id: evt.id,
        userId: evt.userId,
        userName: evt.userName ?? t(msg`Unknown User`),
        userAvatar: evt.userAvatar,
        content: getFeedEventContent(evt),
        timestamp: formatTimestamp(evt.created_at),
        reactions: eventReactions,
      };
    });
  });

  const addReaction = async (eventId: string, emoji: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    try {
      // Filtered by the live layer: without this, a previously removed reaction
      // (server-side soft delete) still appeared here as "existing", and the
      // toggle would "delete" it again (a no-op) instead of inserting the new reaction
      // the user requested — reacting twice to the same emoji was impossible.
      const existing = await dbInstance
        .select()
        .from(reaction)
        .where(
          and(
            eq(reaction.feed_event_id, eventId),
            eq(reaction.user_id, userId()),
            eq(reaction.emoji, emoji),
            ...liveReactionFilters(),
          ),
        )
        .limit(1);

      if (existing.length > 0) {
        const [first] = existing;
        if (first) {
          // Toggle reaction: delete if it already exists
          await dbInstance.delete(reaction).where(eq(reaction.id, first.id));
        }
      } else {
        // Insert new reaction
        await dbInstance.insert(reaction).values({
          id: generateId(),
          feed_event_id: eventId,
          user_id: userId(),
          emoji,
          created_at: new Date().toISOString(),
          deleted_at: null,
        });
      }
    } catch (err) {
      console.error("Failed to transact reaction in SQLite", err);
    }
  };

  const postEvent = async (e: Event) => {
    e.preventDefault();
    const text = newEvent().trim();
    const dbInstance = drizzle();
    if (!text || !dbInstance) {
      return;
    }

    try {
      await dbInstance.insert(feed_event).values({
        id: generateId(),
        workspace_id: workspaceId(),
        user_id: userId(),
        event_type: "status_update",
        event_data: JSON.stringify({ message: text }),
        created_at: new Date().toISOString(),
        deleted_at: null,
      });

      setNewEvent("");
      showToast(t(msg`Status shared successfully`), "success");
    } catch (err) {
      console.error("Failed to insert feed event", err);
    }
  };

  return (
    <PageContainer
      maxWidth="7xl"
      class="space-y-8"
      containerClass="bg-[#000000] text-white pb-[calc(5.5rem+var(--safe-area-inset-bottom))]"
    >
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <PageHeader
          title={t(msg`Team Panel`)}
          description={t(msg`Real-time collaboration status and activities feed.`)}
          icon={Users}
        />
        {/* Fast filter input and invite button */}
        <div class="flex items-center gap-3 w-full md:w-auto shrink-0">
          <WorkspaceSwitcher />
          <div class="relative w-full md:w-64">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search class="h-4 w-4 text-muted-foreground" />
            </span>
            <input
              type="text"
              placeholder={t(msg`Filter team...`)}
              value={filterQuery()}
              onInput={(e) => updateFilterQuery(e.currentTarget.value)}
              class="w-full pl-9 pr-4 py-2 bg-[#121212] border border-white/10 rounded-[16px] text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={openInviteDialog}
            class="bg-primary text-on-primary hover:bg-primary/90 flex items-center gap-2 font-semibold rounded-full cursor-pointer shrink-0"
          >
            {t(msg`Invite Member`)}
          </Button>
        </div>
      </div>

      {/* Banner: invitaciones dirigidas a mi email.
          P23 / CC-60 · I2: sin la guarda `isSuccess`, el `?? []` convertía un
          fallo del API en «no tienes invitaciones» y el banner sencillamente NO
          aparecía: el invitado no llegaba nunca a aceptar y no tenía forma de
          saber por qué. */}
      <Show when={myInvitations().length > 0}>
        <div class="space-y-3">
          <For each={myInvitations()}>
            {(inv) => (
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-[20px] border border-[#afc6ff]/25 bg-[#afc6ff]/5">
                <div class="text-xs font-mono">
                  <span class="font-bold text-[#afc6ff]">{inv.invited_by_name}</span>{" "}
                  <span class="text-foreground/80">
                    {t(msg`invited you to the workspace {workspace} as {role}`, {
                      workspace: inv.workspace_name,
                      role: inv.role,
                    })}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleAcceptInvitation(inv.id)}
                  class="bg-[#afc6ff] text-black hover:bg-[#afc6ff]/90 font-bold rounded-full cursor-pointer shrink-0"
                >
                  {t(msg`Accept invitation`)}
                </Button>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Grid of Team Members */}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <For each={filteredTeamMembers()}>
          {(member) => (
            <GlassSurface
              class={cn(
                "p-5 flex flex-col items-center text-center space-y-4 bg-[#121212] border border-white/10 rounded-[24px] shadow-none hover:border-white/20 transition-colors duration-300",
                member.status === "focus" &&
                  "border-[#ebb2ff]/20 shadow-[0_0_24px_rgba(235,178,255,0.02)]",
              )}
            >
              <div class="relative">
                {/* Status colored Avatar Border */}
                <Avatar
                  userId={member.user_id}
                  name={member.name}
                  image={member.avatar}
                  size="xl"
                  class={cn(
                    "w-16 h-16 rounded-full text-lg border-[3px]",
                    getStatusColor(member.status),
                  )}
                  testId="team-member-avatar"
                />
                {/* Status Dot */}
                <span
                  class={cn(
                    "absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-[#121212]",
                    getStatusBadge(member.status),
                  )}
                />
              </div>

              <div>
                <h4 class="text-sm font-bold text-foreground font-mono mb-2">{member.name}</h4>
                <Select
                  aria-label={t(msg`Role`)}
                  value={member.role}
                  onChange={(value) => {
                    void updateMemberRole(member.id, value);
                  }}
                  class="w-auto rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wider font-mono"
                  options={[
                    { value: "owner", label: t(msg`Owner`) },
                    { value: "admin", label: t(msg`Admin`) },
                    { value: "member", label: t(msg`Member`) },
                  ]}
                />
              </div>

              <div class="w-full pt-1.5 border-t border-white/5">
                <p class="text-[10px] text-muted-foreground uppercase tracking-wider font-bold font-mono">
                  {t(msg`Current Status`)}
                </p>
                <p class="text-xs text-foreground/90 font-medium truncate mt-0.5 max-w-[160px] mx-auto font-mono">
                  {member.activity}
                </p>
              </div>
            </GlassSurface>
          )}
        </For>
      </div>

      {/* Invitaciones pendientes del workspace.
          P23 / CC-60 · I3: con el API caído la tarjeta entera desaparecía y el
          administrador no distinguía «no hay invitaciones pendientes» de «no se
          pudo consultar» — justo antes de reinvitar a alguien que ya lo está. */}
      <Show when={invitationsQuery.isError}>
        <QueryErrorState
          testId="workspace-invitations-error"
          compact
          onRetry={() => void invitationsQuery.refetch()}
        />
      </Show>
      <Show when={pendingInvitations().length > 0}>
        <GlassSurface class="p-5 space-y-3 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
          <h3 class="text-xs font-bold text-muted-foreground uppercase tracking-wider font-mono">
            {t(msg`Pending Invitations`)}
          </h3>
          <For each={pendingInvitations()}>
            {(inv) => (
              <div class="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                <div class="min-w-0 text-xs font-mono">
                  <p class="text-foreground font-bold truncate">{inv.email}</p>
                  <p class="text-muted-foreground text-[10px] uppercase tracking-wider mt-0.5">
                    {t(msg`{role} · invited by {name}`, {
                      role: inv.role,
                      name: inv.invited_by_name,
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void handleCancelInvitation(inv.id)}
                  class="h-8 border-destructive/20 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-xl font-mono text-[10px] uppercase tracking-wider cursor-pointer shrink-0"
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
            )}
          </For>
        </GlassSurface>
      </Show>

      {/* Activity Feed Section */}
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Side: Real-time Updates */}
        <GlassSurface class="lg:col-span-8 p-6 space-y-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
          <div>
            <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
              {t(msg`Live Activity Feed`)}
            </h3>
            <p class="text-xs text-muted-foreground">
              {t(msg`Collaborative milestones and focus logs`)}
            </p>
          </div>

          <div class="space-y-4">
            <For each={combinedFeed()}>
              {(evt) => (
                <div class="flex items-start gap-3.5 p-4 rounded-xl bg-white/5 border border-white/5 transition-colors duration-300">
                  <Avatar
                    userId={evt.userId}
                    name={evt.userName}
                    image={evt.userAvatar}
                    size="sm"
                    class="w-9 h-9 rounded-full text-xs shrink-0"
                  />
                  <div class="flex-1 min-w-0 space-y-2">
                    <div class="flex items-center justify-between gap-2 text-xs">
                      <span class="font-bold text-foreground font-mono">{evt.userName}</span>
                      <span class="text-muted-foreground/70 font-medium font-mono">
                        {evt.timestamp}
                      </span>
                    </div>
                    <p class="text-xs text-muted-foreground font-medium font-mono">{evt.content}</p>

                    {/* Reactions Bar */}
                    <div class="flex flex-wrap items-center gap-2 pt-1.5">
                      <For each={Object.entries(evt.reactions)}>
                        {([emoji, count]) => (
                          <button
                            type="button"
                            onClick={() => {
                              void addReaction(evt.id, emoji);
                            }}
                            class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/5 text-[10px] font-bold text-foreground transition-[transform,background-color] will-change-transform active:scale-90 cursor-pointer font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                          >
                            <span>{emoji}</span>
                            <span class="text-muted-foreground">{count}</span>
                          </button>
                        )}
                      </For>
                      {/* Emoji Quick Picker */}
                      <div class="flex items-center gap-1.5 pl-2 border-l border-white/10">
                        <For each={["🔥", "👍", "🚀", "🎉", "🧠"]}>
                          {(emoji) => (
                            <button
                              type="button"
                              onClick={() => {
                                void addReaction(evt.id, emoji);
                              }}
                              class="text-xs grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-[transform,opacity,filter] will-change-transform hover:scale-125 active:scale-90 cursor-pointer"
                            >
                              {emoji}
                            </button>
                          )}
                        </For>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>
        </GlassSurface>

        {/* Right Side: Share Status */}
        <GlassSurface class="lg:col-span-4 p-6 space-y-5 bg-[#121212] border-white/10">
          <div>
            <h3 class="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
              {t(msg`Share Update`)}
            </h3>
            <p class="text-xs text-muted-foreground">{t(msg`Broadcast what you are working on`)}</p>
          </div>

          <form
            onSubmit={(e) => {
              void postEvent(e);
            }}
            class="space-y-4"
          >
            <textarea
              value={newEvent()}
              onInput={(e) => setNewEvent(e.currentTarget.value)}
              placeholder={t(msg`e.g. Debugging sqlite power sync sync loops...`)}
              rows="3"
              class="w-full bg-background/50 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-foreground placeholder-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed font-mono"
            />
            <Button
              type="submit"
              disabled={!drizzle() || !newEvent().trim()}
              class="w-full bg-[#afc6ff] text-black font-bold flex items-center justify-center gap-2 hover:bg-[#afc6ff]/90 cursor-pointer"
            >
              <Send class="h-4 w-4" /> {t(msg`Share Status`)}
            </Button>
          </form>
        </GlassSurface>
      </div>

      {/* Dialog de Invitación de Miembro */}
      <Dialog
        open={isInviteOpen()}
        onOpenChange={(open) => {
          setIsInviteOpen(open);
          setInviteDialogUrl(open);
        }}
        contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-md"
      >
        <div class="p-6 text-white">
          <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
            <DialogTitle class="text-xl font-bold font-mono">{t(msg`Invite Member`)}</DialogTitle>
            <DialogDescription class="text-xs text-white/60 font-mono mt-1">
              {t(msg`Send an invitation to add a new member to your workspace.`)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleInviteSubmit} class="space-y-5">
            <div class="space-y-1">
              <label
                for="invite-email"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Email Address`)}
              </label>
              <input
                id="invite-email"
                type="email"
                // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
                placeholder="sarah@company.com"
                value={inviteEmail()}
                onInput={(e) => setInviteEmail(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono"
              />
            </div>

            <div class="space-y-1">
              <label
                for="invite-role"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Workspace Role`)}
              </label>
              <Select
                id="invite-role"
                aria-label={t(msg`Workspace Role`)}
                value={inviteRole()}
                onChange={setInviteRole}
                class="font-mono"
                options={[
                  { value: "member", label: t(msg`Member (Collaborator)`) },
                  { value: "admin", label: t(msg`Admin (Administrator)`) },
                  { value: "viewer", label: t(msg`Viewer (Observer)`) },
                ]}
              />
            </div>

            <div class="flex justify-end gap-3 pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={closeInviteDialog}
                class="px-5 py-2.5 rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer border border-white/5 hover:bg-white/5"
              >
                {t(msg`Cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={isInviting()}
                class="px-6 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-primary/95"
              >
                {isInviting() ? t(msg`Inviting...`) : t(msg`Invite`)}
              </Button>
            </div>
          </form>
        </div>
      </Dialog>
    </PageContainer>
  );
}

interface TeamSearch {
  q?: string;
  dialog?: "invite";
  wsdialog?: "new";
  /** Inherited from chronosRoute; we preserve it when rewriting the search. */
  ws?: string;
}

export const chronosTeamRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/team",
  validateSearch: (raw: Record<string, unknown>): TeamSearch => ({
    q: typeof raw.q === "string" && raw.q.trim() !== "" ? raw.q : undefined,
    dialog: raw.dialog === "invite" ? "invite" : undefined,
    wsdialog: raw.wsdialog === "new" ? "new" : undefined,
  }),
  component: TeamPage,
});
